import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import {
  ArrowLeft,
  Save,
  Clock,
  FileText,
  Users,
  Settings
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const AddOnlineTest = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editTestId, setEditTestId] = useState(null)
  const [formData, setFormData] = useState({
    testName: '',
    startDate: '',
    endDate: '',
    testStartTime: '',
    testStartPeriod: 'AM',
    testCloseTime: '',
    testClosePeriod: 'PM',
    duration: '60 min', // default duration in text format
    totalMarks: 0,
    totalQuestions: 0
  })

  const { addOnlineTest, getOnlineTests, updateDocument } = useFirebase()

  // Check if we're in edit mode and load test data
  useEffect(() => {
    const editId = searchParams.get('edit')
    const testName = searchParams.get('testName')

    if (editId) {
      setIsEditMode(true)
      setEditTestId(editId)
      loadTestData(editId, testName)
    }
  }, [searchParams])

  const loadTestData = async (testId, testName) => {
    try {
      setLoading(true)
      const tests = await getOnlineTests()
      const test = tests.find(t => t.id === testId)

      if (test) {
        // Helper function to format date for date input
        const formatDateForInput = (dateValue) => {
          if (!dateValue) return ''
          try {
            let date
            if (dateValue.toDate && typeof dateValue.toDate === 'function') {
              date = dateValue.toDate()
            } else {
              date = new Date(dateValue)
            }
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            return `${year}-${month}-${day}`
          } catch (error) {
            return ''
          }
        }

        setFormData({
          testName: test.testName || testName || '',
          startDate: formatDateForInput(test.startDate),
          endDate: formatDateForInput(test.endDate),
          testStartTime: test.testStartTime ? test.testStartTime.split(' ')[0] : '',
          testStartPeriod: (test.testStartTime && test.testStartTime.includes('PM')) ? 'PM' : 'AM',
          testCloseTime: test.testCloseTime ? test.testCloseTime.split(' ')[0] : '',
          testClosePeriod: (test.testCloseTime && test.testCloseTime.includes('PM')) ? 'PM' : 'AM',
          duration: test.duration ? `${test.duration} min` : '60 min',
          totalMarks: test.totalMarks || 0,
          totalQuestions: test.totalQuestions || 0
        })
      } else {
        toast.error('Test not found')
        navigate('/home?tab=online')
      }
    } catch (error) {
      console.error('Error loading test data:', error)
      toast.error('Failed to load test data')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Helper function to parse duration from text input
  const parseDuration = (durationText) => {
    if (!durationText) return 60 // default to 60 minutes

    // Extract numbers from the text
    const match = durationText.toString().match(/\d+/)
    if (match) {
      return parseInt(match[0])
    }

    return 60 // default to 60 if no number found
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.testName.trim()) {
      toast.error('Please enter a test name')
      return
    }

    if (!formData.startDateTime || !formData.endDateTime) {
      toast.error('Please enter start and end date/time')
      return
    }

    if (!formData.duration) {
      toast.error('Please enter test duration')
      return
    }

    if (!formData.startDate || !formData.endDate) {
      toast.error('Please enter start and end date')
      return
    }

    if (!formData.duration) {
      toast.error('Please enter test duration')
      return
    }

    if (!formData.testStartTime || !formData.testCloseTime) {
      toast.error('Please enter test start and close time')
      return
    }

    try {
      setLoading(true)

      // Convert 12-hour values to 24-hour for the Date object
      const get24HourTime = (timeStr, period) => {
        if (!timeStr) return '00:00';
        const [hours, minutes] = timeStr.split(':');
        let h = parseInt(hours);
        if (period === 'PM' && h !== 12) h += 12;
        if (period === 'AM' && h === 12) h = 0;
        return `${h.toString().padStart(2, '0')}:${minutes}`;
      };

      const start24 = get24HourTime(formData.testStartTime, formData.testStartPeriod);
      const close24 = get24HourTime(formData.testCloseTime, formData.testClosePeriod);

      // Create start datetime
      const startDateTime = new Date(formData.startDate + 'T' + start24)

      // Create end datetime
      const endDateTime = new Date(formData.endDate + 'T' + close24)

      // Validate that end datetime is after start datetime
      if (endDateTime <= startDateTime) {
        toast.error('Test close time must be after test start time')
        setLoading(false)
        return
      }

      // Parse duration from text input
      const durationInMinutes = parseDuration(formData.duration)

      const testData = {
        testName: formData.testName,
        startDate: startDateTime,
        endDate: endDateTime,
        testStartTime: `${formData.testStartTime} ${formData.testStartPeriod}`,
        testCloseTime: `${formData.testCloseTime} ${formData.testClosePeriod}`,
        duration: durationInMinutes,
        totalMarks: parseInt(formData.totalMarks) || 0,
        totalQuestions: parseInt(formData.totalQuestions) || 0,
        questionIds: [],
        updatedAt: new Date()
      }

      if (isEditMode && editTestId) {
        // Update existing test
        await updateDocument('onlineTests', editTestId, testData)
        toast.success('Online test updated successfully!')
        navigate('/home?tab=online')
      } else {
        // Create new test
        testData.createdAt = new Date()

        const testId = await addOnlineTest(testData)
        toast.success('Online test created successfully!')
        navigate(`/add-questions/${testId}?testName=${encodeURIComponent(formData.testName)}&from=online`)
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} online test:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} online test`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/home?tab=online')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditMode ? 'Edit Online Test' : 'Create Online Test'}
            </h1>
            <p className="text-gray-600">
              {isEditMode ? 'Update test details and settings' : 'Set up a new online examination'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Test Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Test Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Name *
                </label>
                <input
                  type="text"
                  name="testName"
                  value={formData.testName}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="Enter test name..."
                  required
                />
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  className="input w-full"
                  required
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="input w-full"
                  required
                />
              </div>

              {/* Test Start Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Test Start Time *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="time"
                      name="testStartTime"
                      value={formData.testStartTime}
                      onChange={handleInputChange}
                      className="input w-full pr-10"
                      required
                    />
                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <select
                    name="testStartPeriod"
                    value={formData.testStartPeriod}
                    onChange={handleInputChange}
                    className="input w-24 font-semibold text-center"
                    required
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Time when students can start the test
                </p>
              </div>

              {/* Test Close Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Test Close Time *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="time"
                      name="testCloseTime"
                      value={formData.testCloseTime}
                      onChange={handleInputChange}
                      className="input w-full pr-10"
                      required
                    />
                    <Clock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <select
                    name="testClosePeriod"
                    value={formData.testClosePeriod}
                    onChange={handleInputChange}
                    className="input w-24 font-semibold text-center"
                    required
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Time after which students cannot attend the test
                </p>
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Clock className="h-4 w-4 inline mr-1" />
                  Test Duration *
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleInputChange}
                  className="input w-full"
                  placeholder="e.g., 30 min, 60 min, 90 min"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter duration in minutes (e.g., "30 min", "60 min", "90 min")
                </p>
              </div>


              {/* Total Questions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Questions
                </label>
                <input
                  type="number"
                  name="totalQuestions"
                  value={formData.totalQuestions}
                  onChange={handleInputChange}
                  className="input w-full"
                  min="0"
                  max="100"
                />
              </div>

              {/* Total Marks */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Marks
                </label>
                <input
                  type="number"
                  name="totalMarks"
                  value={formData.totalMarks}
                  onChange={handleInputChange}
                  className="input w-full"
                  min="0"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/home?tab=online')}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <LoadingSpinner size="small" />
                    {isEditMode ? 'Updating Test...' : 'Creating Test...'}
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    {isEditMode ? 'Update Test' : 'Create Test'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default AddOnlineTest